import { supabase } from '@/services/supabase/client';

/**
 * "Esta conta ainda pode usar o app?" — o estado de acesso de quem está logado.
 *
 * ## Por que existe, se o banco já barra tudo
 *
 * A migration `account_suspension` já fecha toda escrita de uma conta
 * bloqueada (RLS e os cinco helpers de vínculo de instituição). Sem esta
 * consulta, uma conta bloqueada abriria o app normalmente e só descobriria o
 * bloqueio no primeiro toque que falhasse — um pedido de socorro que não sai,
 * um botão que não faz nada. `my_access_state()` é o que permite explicar
 * **antes**: o guard `RequireActiveAccount` a consulta no bootstrap e mostra a
 * tela de conta bloqueada em vez de deixar a pessoa descobrir por tentativa e
 * erro.
 *
 * ⚠️ **Isto não é autorização.** Quem barra é o banco (D-04 do plano); esta
 * função só existe para a explicação, e um valor forjado no cliente não abre
 * porta nenhuma — a RLS continua recusando toda escrita.
 *
 * ## Por que também devolve a instituição bloqueada
 *
 * `institution_suspended_at` some da gestão sem aviso nenhum para quem
 * administra: `is_institution_admin()` passa a responder `false` e a tela
 * trataria isso como "você não é admin". `suspendedInstitutionName` é o que
 * permite ao módulo de instituições mostrar o motivo real.
 */

export interface AccountAccessState {
  isAccountSuspended: boolean;
  /**
   * O nome da instituição de vínculo ativo que está bloqueada, se houver.
   * `null` quando não há sessão, quando a conta não tem vínculo ativo, ou
   * quando o vínculo existe mas não está bloqueado — a RPC já filtra isso e
   * devolve zero linhas de instituição nesses casos.
   */
  suspendedInstitutionName: string | null;
}

export const accountAccessService = {
  /**
   * Zero linhas quando não há sessão — e o cliente lê isso como "sem
   * bloqueio", que é a leitura correta: sem conta não há o que bloquear.
   */
  async myAccessState(): Promise<AccountAccessState> {
    const { data, error } = await supabase.rpc('my_access_state');

    if (error) throw error;

    const row = data?.[0];

    return {
      isAccountSuspended: row?.account_suspended ?? false,
      suspendedInstitutionName: row?.institution_name ?? null,
    };
  },
};
